import { Suspense, lazy, memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ChevronDown, ChevronUp, Download, HelpCircle, Maximize2, Plus, Trash2, Upload } from 'lucide-react'
import { toast } from 'sonner'
import useStore from '@/stores/useStore'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NumberInput } from '@/components/ui/number-input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  downloadCsvTemplate,
  exportEmployeesToCsv,
  parseCsvFile,
} from '@/lib/csv'
import { exportDownloadProps, trackExportEvent } from '@/lib/analytics/exportEvents'
import CensusStartOptions from '@/components/onboarding/CensusStartOptions'
import { headersAllRecognized } from '@/lib/censusMapping'
import { isCsvFile, isSpreadsheetFile } from '@/lib/spreadsheet'
import { importWithReload } from '@/lib/lazyWithReload'
import EstimatorDialog from '@/components/estimator/EstimatorDialog'
import FeatureLockDialog from '@/components/FeatureLockDialog'
import { EXAMPLE_EMPLOYEES } from '@/lib/constants'
import CsvColumnGuide from '@/components/CsvColumnGuide'
import { hasEnabledCashBalancePlan, peopleInputColumnCount } from '@/lib/peopleColumnGates'
import {
  CENSUS_MODES,
  DEMO_EMPLOYEE_LIMIT,
  classifyDemoImport,
  getDemoCensusUsage,
  isDemoCensusAllowed,
} from '@/lib/demoEntitlements'
import { escapeCssValue, flashInputTarget, generateId, parseLocalDate } from '@/lib/utils'
import { FOCUS_EMPLOYEE_INPUT_EVENT, employeeRowKey } from '@/lib/employeeFocus'
import {
  LOAD_CENSUS_EVENT,
  LOAD_EXAMPLE_CENSUS_EVENT,
  SAVE_CENSUS_EVENT,
} from '@/lib/saveLoadEvents'
import { useEmployeeTableView } from '@/hooks/useEmployeeTableView'
import SortableHeader from '@/components/employee-table/SortableHeader'
import EmployeeTableControls from '@/components/employee-table/EmployeeTableControls'
import EmptyState from '@/components/employee-table/EmptyState'
import ClassAssignPopover from '@/components/employee-table/ClassAssignPopover'
import { projectClassAssignment, projectIndividualClasses } from '@/lib/allocationClasses'
import { Expandable, ExpandableScroll, ExpandableFill, PaginatedRows, ShowMoreBar, ScrollHint, useExpandable, useExpanded } from '@/components/ui/expandable'

// The Studio pulls in react-data-grid + (for Excel files) SheetJS, so it stays
// out of the main bundle until someone actually opens it.
const ImportStudioDialog = lazy(() => importWithReload(() => import('@/components/import/ImportStudioDialog')))

const HEADER_HELP = {
  id: 'Unique identifier for the employee record. Auto-generated on CSV import if left blank.',
  name: 'Optional employee name. This field is completely optional.',
  dob: 'Date of birth used for reporting and age-sensitive calculations.',
  hire_date: 'Used to calculate tenure if tenure is not provided. If tenure is entered, tenure overrides hire date for service calculation.',
  hours_served: 'Hours worked in the plan year. Blank or invalid hours remain unknown; zero is treated as a real value. Missing required hours make employer eligibility unknown.',
  compensation: 'Compensation used for deferrals and employer contributions. Proration ON expects an annualized salary rate. Proration OFF expects actual plan-year or YTD compensation.',
  prior_year_compensation: 'Prior-year compensation used for HCE classification. When provided, compared to the prior-year threshold. When blank, current compensation is used as an estimate.',
  cb_participant_entry_date: 'Cash-balance plan participation date when it differs from hire date or the selected preset. In the new-plan scenario, blank uses the later of plan effective date or hire date and is labeled as a preset fallback, not an adopted term.',
  high_3_compensation: 'Explicit highest consecutive 3-year average compensation for cash-balance benefit-limit screening. In the new-plan scenario, blank uses current compensation and is labeled as a preset fallback.',
  section_415_compensation: 'Employee §415 compensation when it differs from their main compensation. If blank, the calculator uses current compensation and marks it as an estimate.',
  pretax_percent: 'Employee pre-tax deferral rate as a percentage of compensation. Only one of pre-tax % or pre-tax $ is needed, and the dollar amount overrides the percentage.',
  pretax_amount: 'Employee pre-tax deferral as a dollar amount. Only one of pre-tax $ or pre-tax % is needed, and the dollar amount overrides the percentage.',
  roth_percent: 'Employee Roth deferral rate as a percentage of compensation. Only one of Roth % or Roth $ is needed, and the dollar amount overrides the percentage.',
  roth_amount: 'Employee Roth deferral as a dollar amount. Only one of Roth $ or Roth % is needed, and the dollar amount overrides the percentage.',
  status: 'Authoritative employment status. Active and Leave remain non-terminated even when a termination date is populated. Only Terminated records receive termination treatment.',
  full_time: 'Full-time flag (Y/N). Part-time employees (N or blank) with 500+ hours and 2+ years of service can make employee deferrals as SECURE 2.0 long-term part-time participants even when they miss the plan\'s eligibility rules; they get no employer contributions.',
  gender: 'Optional gender field for demographic reporting. Use M, F, or leave blank.',
  tenure: 'Optional service override. Use years and months (e.g. 5y 6m). A valid override takes precedence over hire date. Blank or invalid service remains unknown.',
  termination_date: 'Used only when Status is Terminated. A usable date sets age, service, vesting, turnover, and termination-proration timing; otherwise plan-year end is used for valuation with a warning.',
  balance: 'Current account balance used for reporting and projections.',
  loan_balance: 'Outstanding participant loan balance, if any.',
  key_employee: 'Choose Y or N for every employee included in plan metrics. Top-heavy is unavailable when any included employee is blank or when total balance is zero.',
  five_percent_owner: 'More-than-5% owner (Y or N). A Y makes the employee both an HCE (for ADP/ACP/coverage) and a key employee (for top-heavy) regardless of pay, so a low-paid owner is not mis-classified. Leave blank when not an owner — ownership can also come from Own % or Family Of.',
  ownership_percent: 'Direct ownership percentage (0-100). Anything above 5 makes the employee a more-than-5% owner (HCE + key employee regardless of pay) without needing the Y flag. Exactly 5% does not — the statute says MORE than 5%.',
  family_of: 'Name or ID of a more-than-5% owner this employee is the spouse, child, parent, or grandparent of. §318 family attribution then makes this employee a deemed more-than-5% owner (HCE + key) regardless of pay. A grandchild or sibling of the owner is NOT attributed — leave blank for those.',
  allocation_group: 'Allocation class used for class-based match, new comparability, and cash balance formulas. Blank employees use the default class.',
  after_tax_percent: 'After-tax (non-Roth) deferral as a percentage of compensation, for mega-backdoor Roth. Counts toward the 415(c) cap but not the elective deferral limit. Ignored when the plan does not allow after-tax, or when an after-tax $ amount is provided.',
  after_tax_amount: 'After-tax (non-Roth) deferral as a dollar amount (overrides after-tax %). Filled up to the 415(c) cap after employee deferrals and employer contributions.',
}

// Rows-per-page in the popped-out editor. Each input row mounts ~20 controlled
// fields, so mounting a whole large census in one synchronous commit spikes
// memory enough to crash the renderer ("Aw, Snap"). Cap the page so a single
// commit is bounded; the pagination controls step through the rest.
const EMPLOYEE_INPUT_PAGE_SIZE = 25
const EMPLOYEE_INPUT_INITIAL_RENDER_ROWS = 12
const EMPLOYEE_INPUT_RENDER_CHUNK_SIZE = 8
const EMPLOYEE_ID_REQUIRED_MESSAGE = 'All employees must have an ID.'

// A maximize button that sits inline with a toolbar's other buttons instead of
// floating absolutely over the Expandable, so it stays vertically aligned no
// matter how the row wraps. Must render inside an <Expandable>. Renders as
// null once already popped out, since there's nothing left to expand into.
function ExpandTrigger({ label = 'Expand' }) {
  const expanded = useExpanded()
  const { setOpen } = useExpandable()
  if (expanded) return null
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="shrink-0"
      onClick={() => setOpen(true)}
      aria-label={label}
      title={label}
    >
      <Maximize2 className="h-3.5 w-3.5" />
    </Button>
  )
}

function PaginationControls({ page, pageCount, total, start, end, onPageChange }) {
  // Inline the table is capped to a handful of rows (RowLimit), so paging only
  // makes sense in the expanded pop-out where every row renders.
  const expanded = useExpanded()
  if (!expanded || total <= EMPLOYEE_INPUT_PAGE_SIZE) return null

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
      <span>
        Showing {start + 1}-{end} of {total}
      </span>
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7"
          onClick={() => onPageChange(Math.max(0, page - 1))}
          disabled={page === 0}
        >
          Previous
        </Button>
        <span className="min-w-[4rem] text-center">
          Page {page + 1} of {pageCount}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-7"
          onClick={() => onPageChange(Math.min(pageCount - 1, page + 1))}
          disabled={page >= pageCount - 1}
        >
          Next
        </Button>
      </div>
    </div>
  )
}

// The tooltip help affordance on its own, for composing into a SortableHeader's
// `help` slot (the sortable button renders the label, this renders the icon).
function HelpIcon({ label, help }) {
  if (!help) return null

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className="-m-1.5 inline-flex h-6 w-6 items-center justify-center text-muted-foreground/70 hover:text-foreground"
          aria-label={`${label} help`}
        >
          <HelpCircle className="h-3 w-3" />
        </button>
      </TooltipTrigger>
      <TooltipContent className="max-w-[240px]">
        <p>{help}</p>
      </TooltipContent>
    </Tooltip>
  )
}

function parseDateSortValue(value) {
  return parseLocalDate(value)?.getTime() ?? null
}

function parseTenureSortValue(value) {
  if (value === null || value === undefined) return null
  const normalized = String(value).trim().toLowerCase()
  if (!normalized) return null

  if (/^\d+(\.\d+)?$/.test(normalized)) {
    const months = parseFloat(normalized)
    return Number.isNaN(months) ? null : months
  }

  let totalMonths = 0
  let matched = false
  const matches = normalized.matchAll(/(\d+)\s*(y|yr|yrs|year|years|m|mo|mos|month|months|d|day|days)\b/g)

  for (const match of matches) {
    const amount = parseInt(match[1], 10)
    const unit = match[2]
    if (!Number.isFinite(amount)) continue

    matched = true
    if (unit.startsWith('y')) totalMonths += amount * 12
    else if (unit.startsWith('m')) totalMonths += amount
    else if (unit.startsWith('d')) totalMonths += amount / 30
  }

  return matched ? totalMonths : null
}

function EstimateTableColumns({ compact, showCbColumns }) {
  const column = (visible = true) => <col className={compact && !visible ? 'collapse' : undefined} />

  return (
    <colgroup>
      {column()}{/* select */}
      {column()}{/* ID */}
      {column(false)}{/* name */}
      {column()}{/* date of birth */}
      {column()}{/* hire date */}
      {column()}{/* hours */}
      {column()}{/* compensation */}
      {column(false)}{/* prior-year compensation */}
      {showCbColumns && <>{column(false)}{column(false)}{column(false)}</>}
      {column()}{/* pre-tax % */}
      {column(false)}{/* Roth % */}
      {column()}{/* status */}
      {column(false)}{/* full time */}
      {column()}{/* key employee */}
      {column()}{/* owner */}
      {column(false)}{/* ownership % */}
      {column(false)}{/* family attribution */}
      {column(false)}{/* allocation group */}
      {column(false)}{/* gender */}
      {column(false)}{/* tenure override */}
      {column(false)}{/* termination date */}
      {column(false)}{/* pre-tax $ */}
      {column(false)}{/* Roth $ */}
      {column(false)}{/* balance */}
      {column(false)}{/* loan balance */}
      {column(false)}{/* after-tax % */}
      {column(false)}{/* after-tax $ */}
    </colgroup>
  )
}

export default function DataInput({ demoMode = false, studioHref = null, loginHref = null }) {
  const employees = useStore((state) => state.employees)
  const censusMode = useStore((state) => state.censusMode)
  const censusBaseline = useStore((state) => state.censusBaseline)
  const workspaceMode = useStore((state) => state.workspaceMode)
  const planYear = useStore((state) => state.planA.planYear)
  // Cash-balance participant columns only matter once some plan slot actually
  // has a cash-balance design.
  const showCbColumns = useStore((state) => (
    hasEnabledCashBalancePlan([state.planA, state.planB, state.planC, state.planD])
  ))
  const employeeTableColumnCount = peopleInputColumnCount({ showCbColumns })
  const setEmployees = useStore((state) => state.setEmployees)
  const updateEmployee = useStore((state) => state.updateEmployee)
  const deleteAllEmployees = useStore((state) => state.deleteAllEmployees)
  const loadExampleData = useStore((state) => state.loadExampleData)
  const setEmployeeClasses = useStore((state) => state.setEmployeeClasses)
  const setIndividualEmployeeClasses = useStore((state) => state.setIndividualEmployeeClasses)

  const [isColumnGuideOpen, setIsColumnGuideOpen] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [employeeTableOpen, setEmployeeTableOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [currentPage, setCurrentPage] = useState(0)
  const [demoDialog, setDemoDialog] = useState(null)
  // null = closed; { files: File[] } = open (empty array shows the Studio's own drop zone)
  const [studio, setStudio] = useState(null)
  // A parsed import waiting on the "replace your current list?" confirmation.
  // { result, sourceFile } – only set when a custom census would be replaced.
  const [pendingImport, setPendingImport] = useState(null)
  const [estimatorOpen, setEstimatorOpen] = useState(false)
  const [showEstimateDetails, setShowEstimateDetails] = useState(false)
  // 'studio' | 'estimator' | null – demo users see the features but they are
  // login-gated (the Studio and estimator are where the AI features live).
  const [featureLock, setFeatureLock] = useState(null)
  const compactEstimateView = workspaceMode === 'estimate' && !showEstimateDetails

  // Single choke point for every route into the import review dialog.
  const openStudio = useCallback((files) => {
    if (demoMode) {
      setFeatureLock('studio')
      return
    }
    setStudio({ files })
  }, [demoMode])

  const openEstimator = useCallback(() => {
    if (demoMode) {
      setFeatureLock('estimator')
      return
    }
    setEstimatorOpen(true)
  }, [demoMode])
  const fileInputRef = useRef(null)
  const processFilesRef = useRef(null)
  const employeeTableRef = useRef(null)
  const shouldScrollToAddedEmployeeRef = useRef(false)
  const pendingEmployeeFocusRef = useRef(null)
  const demoUsage = useMemo(
    () => getDemoCensusUsage(censusMode, employees, censusBaseline),
    [censusBaseline, censusMode, employees],
  )

  // Sort descriptors for every sortable column (numeric + text). Keys are stable
  // ids passed to SortableHeader/toggleSort.
  const columns = useMemo(() => ({
    // Text columns
    id: { type: 'text', accessor: (emp) => emp.id },
    name: { type: 'text', accessor: (emp) => emp.name },
    status: { type: 'text', accessor: (emp) => emp.status || 'Active' },
    gender: { type: 'text', accessor: (emp) => emp.gender },
    allocationGroup: { type: 'text', accessor: (emp) => emp.allocation_group || '' },
    // Numeric columns
    dob: { type: 'number', accessor: (emp) => parseDateSortValue(emp.dob) },
    hireDate: { type: 'number', accessor: (emp) => parseDateSortValue(emp.hire_date) },
    hours: { type: 'number', accessor: (emp) => emp.hours_served },
    compensation: { type: 'number', accessor: (emp) => emp.compensation },
    priorYearComp: { type: 'number', accessor: (emp) => emp.prior_year_compensation },
    cbParticipantEntryDate: { type: 'number', accessor: (emp) => parseDateSortValue(emp.cb_participant_entry_date) },
    high3Compensation: { type: 'number', accessor: (emp) => emp.high_3_compensation },
    section415Compensation: { type: 'number', accessor: (emp) => emp.section_415_compensation },
    fullTime: { type: 'text', accessor: (emp) => emp.full_time || '' },
    keyEmployee: { type: 'text', accessor: (emp) => emp.key_employee || '' },
    fivePercentOwner: { type: 'text', accessor: (emp) => emp.five_percent_owner || '' },
    ownershipPercent: { type: 'number', accessor: (emp) => emp.ownership_percent },
    familyOf: { type: 'text', accessor: (emp) => emp.family_of || '' },
    pretaxPercent: { type: 'number', accessor: (emp) => emp.pretax_percent },
    rothPercent: { type: 'number', accessor: (emp) => emp.roth_percent },
    tenure: { type: 'number', accessor: (emp) => parseTenureSortValue(emp.tenure) },
    terminationDate: { type: 'number', accessor: (emp) => parseDateSortValue(emp.termination_date) },
    pretaxAmount: { type: 'number', accessor: (emp) => emp.pretax_amount },
    rothAmount: { type: 'number', accessor: (emp) => emp.roth_amount },
    balance: { type: 'number', accessor: (emp) => emp.balance },
    loanBalance: { type: 'number', accessor: (emp) => emp.loan_balance },
    afterTaxPercent: { type: 'number', accessor: (emp) => emp.after_tax_percent },
    afterTaxAmount: { type: 'number', accessor: (emp) => emp.after_tax_amount },
  }), [])

  const allocationGroupOptions = useMemo(() => (
    [...new Set(employees.map((emp) => String(emp.allocation_group || '').trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
  ), [employees])

  // Filter definitions for the discrete-valued columns.
  const filters = useMemo(() => ([
    {
      key: 'status',
      label: 'Status',
      getValue: (emp) => emp.status || 'Active',
      options: ['Active', 'Leave', 'Terminated'],
    },
    {
      key: 'allocationGroup',
      label: 'Allocation Class',
      getValue: (emp) => String(emp.allocation_group || '').trim(),
      options: allocationGroupOptions,
    },
  ]), [allocationGroupOptions])

  const getSearchText = useMemo(
    () => (emp) => `${emp.id ?? ''} ${emp.name ?? ''} ${emp.allocation_group ?? ''}`,
    []
  )

  const {
    viewRows,
    search,
    setSearch,
    activeFilters,
    setFilter,
    clearAll,
    sort,
    toggleSort,
    totalCount,
    activeCount,
    isActive,
  } = useEmployeeTableView({
    rows: employees,
    getSearchText,
    filters,
    columns,
    initialSort: { key: 'compensation', dir: 'desc' },
  })

  const pageCount = Math.max(1, Math.ceil(viewRows.length / EMPLOYEE_INPUT_PAGE_SIZE))
  const activePage = Math.min(currentPage, pageCount - 1)
  const pageStart = activePage * EMPLOYEE_INPUT_PAGE_SIZE
  const pageEnd = Math.min(viewRows.length, pageStart + EMPLOYEE_INPUT_PAGE_SIZE)
  const visibleEmployees = useMemo(
    () => viewRows.slice(pageStart, pageEnd),
    [viewRows, pageStart, pageEnd]
  )
  const employeeIndexByRef = useMemo(
    () => new Map(employees.map((employee, index) => [employee, index])),
    [employees],
  )
  const pendingFocusEmployee = Number.isInteger(pendingEmployeeFocusRef.current?.employeeIndex)
    ? employees[pendingEmployeeFocusRef.current.employeeIndex]
    : null
  const pendingFocusViewIndex = pendingFocusEmployee ? viewRows.indexOf(pendingFocusEmployee) : -1
  const pendingFocusVisibleIndex = pendingFocusViewIndex >= pageStart && pendingFocusViewIndex < pageEnd
    ? pendingFocusViewIndex - pageStart
    : null

  useEffect(() => {
    const handleFocusEmployeeInput = (event) => {
      const target = event.detail
      if (!target?.validationId) return
      pendingEmployeeFocusRef.current = target
      clearAll()
      setEmployeeTableOpen(true)
    }

    window.addEventListener(FOCUS_EMPLOYEE_INPUT_EVENT, handleFocusEmployeeInput)
    return () => window.removeEventListener(FOCUS_EMPLOYEE_INPUT_EVENT, handleFocusEmployeeInput)
  }, [clearAll])

  useEffect(() => {
    const target = pendingEmployeeFocusRef.current
    if (!target || !employeeTableOpen) return

    const targetIndex = Number.isInteger(target.employeeIndex)
      ? viewRows.findIndex((employee) => employees.indexOf(employee) === target.employeeIndex)
      : -1

    if (targetIndex >= 0) {
      const nextPage = Math.floor(targetIndex / EMPLOYEE_INPUT_PAGE_SIZE)
      if (activePage !== nextPage) {
        setCurrentPage(nextPage)
        return
      }
    }

    let frame
    let focusTimeout
    let attempts = 0
    const locate = () => {
      const selector = `[data-validation-id="${escapeCssValue(target.validationId)}"]`
      // The row renders twice: inline (capped preview, hidden behind the modal)
      // and inside the popped-out modal. When the modal is open, scope the search
      // to it – otherwise, during the frame before its content mounts, we'd match
      // the inline copy and flash a row nobody can see.
      const modalRoot = document.querySelector('[data-slot="dialog-content"]')
      const element = (modalRoot || document).querySelector(selector)

      if (!element) {
        // The popped-out table mounts a frame after the dialog opens, so retry
        // briefly rather than giving up on the first frame.
        if (attempts++ < 30) frame = requestAnimationFrame(locate)
        return
      }
      element.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' })
      // Flash the whole row yellow (fading out), matching the Outputs table.
      flashInputTarget(element)
      focusTimeout = window.setTimeout(() => element.focus({ preventScroll: true }), 150)
      pendingEmployeeFocusRef.current = null
    }
    frame = requestAnimationFrame(locate)

    return () => {
      cancelAnimationFrame(frame)
      if (focusTimeout) window.clearTimeout(focusTimeout)
    }
  }, [activePage, employeeTableOpen, employees, viewRows])

  // Reset to the first page when the search query or filters change (Req 4.2).
  // Skip while a focus jump is pending: focusing an employee calls clearAll(),
  // which changes the filters and would otherwise reset to page 0 right after
  // the focus effect paged to the target's page – leaving off-page-0 employees
  // (e.g. jumped to from a note) never rendered and never flashed.
  useEffect(() => {
    if (pendingEmployeeFocusRef.current) return
    setCurrentPage(0)
  }, [search, activeFilters])

  // Clamp the active page when the view shrinks (Req 4.4).
  useEffect(() => {
    setCurrentPage((page) => Math.min(page, pageCount - 1))
  }, [pageCount])

  useEffect(() => {
    if (!shouldScrollToAddedEmployeeRef.current) return

    shouldScrollToAddedEmployeeRef.current = false
    setCurrentPage(pageCount - 1)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        employeeTableRef.current?.scrollTo({
          top: employeeTableRef.current.scrollHeight,
          behavior: 'smooth',
        })
      })
    })
  }, [employees.length, pageCount])

  const handleToggleSelect = useCallback((id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const allViewRowsSelected =
    viewRows.length > 0 && viewRows.every((employee) => selectedIds.has(employee.id))

  const handleSelectAll = () => {
    const viewIds = viewRows.map((employee) => employee.id)
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (allViewRowsSelected) {
        viewIds.forEach((id) => next.delete(id))
      } else {
        viewIds.forEach((id) => next.add(id))
      }
      return next
    })
  }

  const handleDeleteSelected = () => {
    const nextEmployees = employees.filter((employee) => !selectedIds.has(employee.id))
    if (!applyDemoCensusChange(nextEmployees)) return
    toast.success(`Deleted ${selectedIds.size} employee${selectedIds.size === 1 ? '' : 's'}`)
    setSelectedIds(new Set())
  }

  const openDemoLimit = useCallback((mode = censusMode) => {
    setDemoDialog({
      type: mode === CENSUS_MODES.CUSTOM ? 'custom-limit' : 'example-limit',
    })
  }, [censusMode])

  const applyDemoCensusChange = useCallback((
    nextEmployees,
    nextMode = censusMode,
    nextBaseline = censusBaseline,
  ) => {
    if (demoMode && !isDemoCensusAllowed(nextMode, nextEmployees, nextBaseline)) {
      openDemoLimit(nextMode)
      return false
    }

    setEmployees(nextEmployees, { censusMode: nextMode, censusBaseline: nextBaseline })
    return true
  }, [censusBaseline, censusMode, demoMode, openDemoLimit, setEmployees])

  const handleUpdateEmployee = useCallback((id, updates) => {
    const nextEmployees = employees.map(employee => (
      employee.id === id ? { ...employee, ...updates } : employee
    ))
    if (demoMode && !isDemoCensusAllowed(censusMode, nextEmployees, censusBaseline)) {
      openDemoLimit()
      return false
    }
    updateEmployee(id, updates)
    return true
  }, [censusBaseline, censusMode, demoMode, employees, openDemoLimit, updateEmployee])

  // Class assignment is a census edit, so it runs the same demo-cap check as
  // every other one. The projection is shared with the store writer, so the rows
  // checked here are exactly the rows that get written.
  const guardCensusProjection = useCallback((nextEmployees, apply) => {
    if (demoMode && !isDemoCensusAllowed(censusMode, nextEmployees, censusBaseline)) {
      openDemoLimit()
      return false
    }
    apply()
    return true
  }, [censusBaseline, censusMode, demoMode, openDemoLimit])

  const handleAssignClass = useCallback((ids, label) => guardCensusProjection(
    projectClassAssignment(employees, ids, label),
    () => setEmployeeClasses(ids, label),
  ), [employees, guardCensusProjection, setEmployeeClasses])

  const handleAssignIndividualClasses = useCallback((ids) => guardCensusProjection(
    projectIndividualClasses(employees, ids),
    () => setIndividualEmployeeClasses(ids),
  ), [employees, guardCensusProjection, setIndividualEmployeeClasses])

  // `sourceFile` (when known) powers a "fix the skipped rows by hand" escalation
  // straight into the import review.
  const showImportToast = (result, importedCount = result.validRows, sourceFile = null) => {
    if (result.skippedRows > 0) {
      const maxToShow = 5
      const details = result.skippedRowDetails.slice(0, maxToShow).map(row =>
        `Row ${row.rowNumber} (${row.id}): ${row.reasons.join(', ')}`
      )

      if (result.skippedRowDetails.length > maxToShow) {
        details.push(`...and ${result.skippedRowDetails.length - maxToShow} more`)
      }

      toast.warning(`Imported ${importedCount} employee${importedCount === 1 ? '' : 's'}`, {
        description: `${result.skippedRows} row${result.skippedRows === 1 ? '' : 's'} skipped:\n${details.join('\n')}`,
        duration: 8000,
        action: sourceFile ? { label: 'Fix skipped rows', onClick: () => openStudio([sourceFile]) } : undefined,
      })
      return
    }

    toast.success(`Imported ${importedCount} employee${importedCount === 1 ? '' : 's'}`)
  }

  const processFiles = async (fileList) => {
    const files = [...(fileList || [])].filter(Boolean)
    if (files.length === 0) return
    // Multiple files (e.g. payroll + recordkeeper) and Excel workbooks always
    // get the full import review – it can edit, map, and combine them.
    if (files.length > 1 || files.some((f) => isSpreadsheetFile(f.name))) {
      openStudio(files)
      return
    }
    const file = files[0]
    if (!isCsvFile(file.name)) {
      toast.error('Invalid file type', {
        description: 'Please upload a CSV or Excel file.',
      })
      return
    }

    setIsUploading(true)
    try {
      const result = await parseCsvFile(file)
      if (demoMode) {
        const classification = classifyDemoImport(result.employees)

        if (classification.kind === 'load') {
          loadCsvResult(result, file, {
            censusMode: classification.censusMode,
            censusBaseline: classification.censusBaseline,
          })
          return
        }

        setDemoDialog({ type: 'csv-oversize', result })
        return
      }

      // Only a CSV the parser fully understands on its own (template headers,
      // no skipped rows) imports directly – with the usual confirm. Anything
      // less obvious goes through the one import review, where the matching
      // and cleanups are visible and adjustable.
      if (!headersAllRecognized(result.headers || []) || result.skippedRows > 0) {
        openStudio([file])
        return
      }

      loadCsvResult(result, file)
    } catch (error) {
      console.error('CSV parsing error:', error)
      toast.error('Failed to parse CSV file', {
        description: 'Please check the file format, or fix it by hand in the import wizard.',
        action: { label: 'Open the importer', onClick: () => openStudio([file]) },
      })
    } finally {
      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }
  processFilesRef.current = processFiles

  const commitCsvResult = (result, sourceFile = null, census = null) => {
    setEmployees(result.employees, census || { censusMode: CENSUS_MODES.CUSTOM, censusBaseline: [] })
    setSelectedIds(new Set())
    showImportToast(result, result.validRows, sourceFile)
  }

  // `census` carries demo-mode's classification overrides through the confirm.
  const loadCsvResult = (result, sourceFile = null, census = null) => {
    // Every upload is confirmed before it touches the census – even over the
    // example data or an empty list. Nothing changes from a stray file drop.
    setPendingImport({ result, sourceFile, census })
  }

  // The import wizard hands back a fully validated parse result (same shape as
  // parseCsvFile), so loading it goes through the same demo gating as a CSV.
  // The wizard stays open after a census import when a plan-settings review is
  // still pending (mixed SPD + CSV upload); it closes itself via onOpenChange.
  // Skipped rows were shown (red cells) and accepted on its Review step, so the
  // toast here stays to one line instead of re-listing them.
  const wizardImportToast = (result) => {
    toast.success(`Imported ${result.validRows} employee${result.validRows === 1 ? '' : 's'}`, {
      description: result.skippedRows > 0
        ? `${result.skippedRows} skipped row${result.skippedRows === 1 ? '' : 's'} stayed out, as reviewed.`
        : undefined,
    })
  }

  const handleStudioCommit = (result) => {
    if (demoMode) {
      const classification = classifyDemoImport(result.employees)
      if (classification.kind === 'load') {
        setEmployees(result.employees, {
          censusMode: classification.censusMode,
          censusBaseline: classification.censusBaseline,
        })
        setSelectedIds(new Set())
        wizardImportToast(result)
        return
      }
      setDemoDialog({ type: 'csv-oversize', result })
      return
    }
    // The wizard is already a deliberate multi-step flow – no second confirm.
    setEmployees(result.employees, { censusMode: CENSUS_MODES.CUSTOM, censusBaseline: [] })
    setSelectedIds(new Set())
    wizardImportToast(result)
  }

  const handleFileUpload = (e) => {
    processFiles(e.target.files)
  }

  const handleAddEmployee = () => {
    const newEmployee = {
      id: generateId(),
      name: '',
      dob: '',
      gender: '',
      hire_date: '',
      tenure: '',
      termination_date: '',
      // Untouched fields start blank, not 0 – blank means "not provided" and
      // must stay blank in the grid and on a census download; only a value the
      // user actually types (including an explicit 0) is real data.
      hours_served: '',
      compensation: '',
      prior_year_compensation: '',
      cb_participant_entry_date: '',
      high_3_compensation: '',
      section_415_compensation: '',
      pretax_percent: '',
      pretax_amount: '',
      roth_percent: '',
      roth_amount: '',
      balance: '',
      loan_balance: '',
      status: 'Active',
      full_time: '',
      key_employee: '',
      five_percent_owner: '',
      ownership_percent: '',
      family_of: '',
      allocation_group: '',
    }
    // Clear any active search/filters so the newly added row is visible (Req 7.3).
    clearAll()
    if (!applyDemoCensusChange([...employees, newEmployee])) return
    shouldScrollToAddedEmployeeRef.current = true
    toast.success('Employee added')
  }

  const handleLoadExamples = () => {
    loadExampleData()
    setSelectedIds(new Set())
    toast.success('Example employees loaded')
  }

  const handleDownloadTemplate = () => {
    downloadCsvTemplate()
    trackExportEvent('download', exportDownloadProps(useStore.getState(), 'census_template_csv'))
  }

  // CSV export (§2.2): allocation_group round-trips as-is. It is the class
  // membership, not a projection of it, so export has nothing to resolve.
  const exportCensusCsv = useCallback(() => {
    exportEmployeesToCsv(viewRows, {
      estimate: useStore.getState().workspaceMode === 'estimate',
    })
  }, [viewRows])

  useEffect(() => {
    const handleSaveCensus = () => exportCensusCsv()
    const handleLoadCensus = (event) => {
      const droppedFiles = event.detail?.files
      if (droppedFiles?.length) {
        processFilesRef.current?.(droppedFiles)
        return
      }
      fileInputRef.current?.click()
    }
    const handleLoadExampleCensus = () => {
      loadExampleData()
      setSelectedIds(new Set())
      toast.success('Example employees loaded')
    }

    window.addEventListener(SAVE_CENSUS_EVENT, handleSaveCensus)
    window.addEventListener(LOAD_CENSUS_EVENT, handleLoadCensus)
    window.addEventListener(LOAD_EXAMPLE_CENSUS_EVENT, handleLoadExampleCensus)
    return () => {
      window.removeEventListener(SAVE_CENSUS_EVENT, handleSaveCensus)
      window.removeEventListener(LOAD_CENSUS_EVENT, handleLoadCensus)
      window.removeEventListener(LOAD_EXAMPLE_CENSUS_EVENT, handleLoadExampleCensus)
    }
  }, [exportCensusCsv, loadExampleData])

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDragEnter = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    processFiles(e.dataTransfer.files)
  }

  const hasEmployees = employees.length > 0

  const loadThreeEmployeePreview = () => {
    const sourceEmployees = demoDialog.result.employees
    const preview = sourceEmployees.slice(0, DEMO_EMPLOYEE_LIMIT)
    setEmployees(preview, { censusMode: CENSUS_MODES.CUSTOM, censusBaseline: [] })
    setSelectedIds(new Set())
    setDemoDialog(null)

    showImportToast(demoDialog.result, preview.length)
  }

  // Shared by the slim (populated) and tall (empty) drop zones – same hidden
  // input + drag handlers, only the layout classes differ.
  const dropZoneProps = {
    htmlFor: 'csv-upload',
    onDragOver: handleDragOver,
    onDragEnter: handleDragEnter,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  }
  const dropZoneStateClass = isDragging
    ? 'border-primary bg-primary/5 text-primary'
    : 'border-border bg-card/80 hover:border-primary/50 hover:bg-accent/50'

  return (
    <section id="data-input" className="space-y-4">
      <Dialog open={Boolean(demoDialog)} onOpenChange={(open) => !open && setDemoDialog(null)}>
        {demoDialog && (
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {demoDialog.type === 'csv-oversize' && `${demoDialog.result.validRows} valid employees found`}
                {demoDialog.type === 'example-limit' && `${DEMO_EMPLOYEE_LIMIT} sample employees are already modified`}
                {demoDialog.type === 'custom-limit' && `Your demo employee list can include ${DEMO_EMPLOYEE_LIMIT} employees`}
              </DialogTitle>
              <DialogDescription>
                {demoDialog.type === 'csv-oversize' && `The demo can load the first ${DEMO_EMPLOYEE_LIMIT} valid employees from this file. Open Studio to upload the full employee list.`}
                {demoDialog.type === 'example-limit' && 'Restore one sample employee, undo a change, or reload the sample before modifying another employee. Additions and deletions also count.'}
                {demoDialog.type === 'custom-limit' && 'Remove an employee before adding another, or open Studio for a larger employee list.'}
              </DialogDescription>
            </DialogHeader>
            <p className="text-xs text-muted-foreground">
              Demo and Studio use separate browser storage, so this demo work will not transfer automatically.
            </p>
            <DialogFooter>
              {demoDialog.type === 'csv-oversize' && (
                <Button type="button" variant="outline" onClick={loadThreeEmployeePreview}>
                  Load first {DEMO_EMPLOYEE_LIMIT} employees
                </Button>
              )}
              {studioHref && (
                <Button asChild>
                  <a href={studioHref}>Open Studio</a>
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        )}
      </Dialog>
      {demoMode && (
        <p className="text-xs text-muted-foreground">
          {workspaceMode === 'estimate'
            ? `Estimated employee list: ${employees.length} generated employees based on your inputs. Edit the essential fields below, or choose Detailed view for every field.`
            : `Explore ${EXAMPLE_EMPLOYEES.length} sample employees. Modify the sample or upload your own employee list.`}
        </p>
      )}

      {demoMode && workspaceMode !== 'estimate' && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 text-xs">
          <span className="text-muted-foreground">
            {censusMode === CENSUS_MODES.CUSTOM
              ? `Your employee list · ${employees.length} employees`
              : `Sample employees · ${demoUsage} employees modified`}
          </span>
          {studioHref && <a href={studioHref} className="font-medium text-primary hover:underline">Need more? Open Studio</a>}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.tsv,.txt,.xlsx,.xls,.xlsm"
        multiple
        onChange={handleFileUpload}
        className="hidden"
        id="csv-upload"
      />

      <AlertDialog open={Boolean(pendingImport)} onOpenChange={(next) => { if (!next) setPendingImport(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {employees.length > 0
                ? `Replace ${employees.length} employee${employees.length === 1 ? '' : 's'} with this file?`
                : `Load ${pendingImport?.result.validRows} employee${pendingImport?.result.validRows === 1 ? '' : 's'} from this file?`}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {employees.length === 0
                ? 'The employees from the file will become your employee list.'
                : censusMode === CENSUS_MODES.CUSTOM
                ? `The ${pendingImport?.result.validRows} employee${pendingImport?.result.validRows === 1 ? '' : 's'} from the new file will replace your current list. You can undo this afterward.`
                : `The ${pendingImport?.result.validRows} employee${pendingImport?.result.validRows === 1 ? '' : 's'} from the new file will replace the built-in example data.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{employees.length > 0 ? 'Keep current list' : 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const pending = pendingImport
                setPendingImport(null)
                if (pending) commitCsvResult(pending.result, pending.sourceFile, pending.census)
              }}
            >
              {employees.length > 0 ? 'Replace list' : 'Load employees'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {!demoMode && <EstimatorDialog open={estimatorOpen} onOpenChange={setEstimatorOpen} />}

      <FeatureLockDialog
        open={Boolean(featureLock)}
        onOpenChange={(next) => { if (!next) setFeatureLock(null) }}
        title={featureLock === 'estimator' ? 'Log in to estimate a plan' : 'Log in to import that file'}
        description={featureLock === 'estimator'
          ? 'The estimator (with AI-assisted assumptions) is available to signed-in users. It models a full plan from rough numbers without an employee file.'
          : 'Excel workbooks, multi-file combining, and the full import review are available to signed-in users. The demo can load a small CSV directly.'}
        loginHref={loginHref || studioHref}
      />

      {studio && (
        <Suspense fallback={null}>
          <ImportStudioDialog
            open
            onOpenChange={(next) => { if (!next) setStudio(null) }}
            initialFiles={studio.files}
            onCommit={handleStudioCommit}
          />
        </Suspense>
      )}

      {hasEmployees && (
        <>
          <label
            {...dropZoneProps}
            className={[
              'flex cursor-pointer items-center justify-between gap-3 rounded-md border border-dashed px-3 py-2.5 text-sm transition-colors',
              dropZoneStateClass,
            ].join(' ')}
          >
            <span className="flex items-center gap-2 text-muted-foreground">
              <Upload className="h-4 w-4" />
              {isUploading ? 'Uploading...' : isDragging ? 'Drop file here' : 'Upload an employee file'}
            </span>
            <span className="hidden text-xs text-muted-foreground sm:inline">
              {censusMode === CENSUS_MODES.CUSTOM
                ? 'Replaces your current list – you’ll confirm first.'
                : 'Replaces the example list.'}
            </span>
          </label>

        </>
      )}

      {hasEmployees ? (
        <Expandable
          className="space-y-2"
          title="Employees"
          description="Edit employee inputs. Changes are saved as you type."
          showTrigger={false}
          modalLayout="fit"
          open={employeeTableOpen}
          onOpenChange={setEmployeeTableOpen}
        >
          <div className="flex flex-wrap items-center gap-2">
            <Button type="button" size="sm" className="h-7" onClick={handleAddEmployee}>
              <Plus className="mr-1.5 h-3.5 w-3.5" />
              Add employee
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7"
              title="Download the employee list you entered as a CSV"
              onClick={exportCensusCsv}
            >
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Download employee list
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button type="button" variant="ghost" size="sm" className="h-7 text-destructive hover:text-destructive">
                  <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                  Clear all
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete all employees?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This removes every employee from the current employee list. You can undo this afterward.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => { deleteAllEmployees(); setSelectedIds(new Set()); toast.success('All employees cleared') }}
                  >
                    Delete all
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            {workspaceMode === 'estimate' && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="ml-auto h-7"
                onClick={() => setShowEstimateDetails((current) => !current)}
                aria-pressed={showEstimateDetails}
              >
                {showEstimateDetails ? 'Show fewer details' : 'Show more details'}
              </Button>
            )}
            <ExpandTrigger label="Expand Employees" />
          </div>

          <ExpandableFill className="space-y-2">
            <EmployeeTableControls
              search={search}
              onSearchChange={setSearch}
              filters={filters}
              activeFilters={activeFilters}
              onFilterChange={setFilter}
              onClear={clearAll}
              isActive={isActive}
              activeCount={activeCount}
              totalCount={totalCount}
              actionAfterFilters={selectedIds.size > 0 && (
                <>
                <ClassAssignPopover
                  selectedIds={selectedIds}
                  onAssign={handleAssignClass}
                  onAssignIndividual={handleAssignIndividualClasses}
                  onAssigned={(message) => { setSelectedIds(new Set()); toast.success(message) }}
                />
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="h-7 text-destructive hover:text-destructive">
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                      Delete selected
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete selected employees?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will remove {selectedIds.size} selected employee{selectedIds.size === 1 ? '' : 's'}.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDeleteSelected} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                </>
              )}
            />
            <PaginationControls
              page={activePage}
              pageCount={pageCount}
              total={viewRows.length}
              start={pageStart}
              end={pageEnd}
              onPageChange={setCurrentPage}
            />
            <ExpandableFill>
            <ExpandableScroll
              id="employee-input-table"
              ref={employeeTableRef}
              className="rounded-md border border-border bg-card"
              collapsedClassName={visibleEmployees.length > 5 ? 'overflow-x-auto rounded-b-none border-b-0' : 'overflow-x-auto'}
              expandedClassName="min-h-0 flex-1 overflow-auto"
            >
              <TooltipProvider>
                <table className="data-table data-table-input">
                  <EstimateTableColumns compact={compactEstimateView} showCbColumns={showCbColumns} />
                  <thead className="sticky top-0 z-10">
                    <tr>
                      <th className="sticky-col sticky-select whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={allViewRowsSelected}
                          onChange={handleSelectAll}
                          className="mt-1"
                          aria-label="Select all employees"
                        />
                      </th>
                      <SortableHeader
                        columnKey="id"
                        active={sort.key === 'id'}
                        dir={sort.key === 'id' ? sort.dir : null}
                        onToggle={toggleSort}
                        className="sticky-col sticky-employee-id min-w-[110px]"
                        help={<HelpIcon label="ID" help={HEADER_HELP.id} />}
                      >
                        ID
                      </SortableHeader>
                      <SortableHeader
                        columnKey="name"
                        active={sort.key === 'name'}
                        dir={sort.key === 'name' ? sort.dir : null}
                        onToggle={toggleSort}
                        className="sticky-col sticky-employee-name min-w-[180px]"
                        help={<HelpIcon label="Name" help={HEADER_HELP.name} />}
                      >
                        Name
                      </SortableHeader>
                      <SortableHeader
                        columnKey="dob"
                        active={sort.key === 'dob'}
                        dir={sort.key === 'dob' ? sort.dir : null}
                        onToggle={toggleSort}
                        className="min-w-[130px]"
                        help={<HelpIcon label="DOB" help={HEADER_HELP.dob} />}
                      >
                        DOB
                      </SortableHeader>
                      <SortableHeader
                        columnKey="hireDate"
                        active={sort.key === 'hireDate'}
                        dir={sort.key === 'hireDate' ? sort.dir : null}
                        onToggle={toggleSort}
                        className="min-w-[130px]"
                        help={<HelpIcon label="Hire Date" help={HEADER_HELP.hire_date} />}
                      >
                        Hire Date
                      </SortableHeader>
                      <SortableHeader
                        columnKey="hours"
                        active={sort.key === 'hours'}
                        dir={sort.key === 'hours' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                        className="min-w-[100px]"
                        help={<HelpIcon label="Hours" help={HEADER_HELP.hours_served} />}
                      >
                        Hours
                      </SortableHeader>
                      <SortableHeader
                        columnKey="compensation"
                        active={sort.key === 'compensation'}
                        dir={sort.key === 'compensation' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                        className="min-w-[140px]"
                        help={<HelpIcon label="Compensation" help={HEADER_HELP.compensation} />}
                      >
                        Compensation
                      </SortableHeader>
                      <SortableHeader
                        columnKey="priorYearComp"
                        active={sort.key === 'priorYearComp'}
                        dir={sort.key === 'priorYearComp' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                        className="min-w-[130px]"
                        help={<HelpIcon label="Prior-Year Compensation" help={HEADER_HELP.prior_year_compensation} />}
                      >
                        Prior-Year Compensation
                      </SortableHeader>
                      {showCbColumns && (
                        <>
                          <SortableHeader
                            columnKey="cbParticipantEntryDate"
                            active={sort.key === 'cbParticipantEntryDate'}
                            dir={sort.key === 'cbParticipantEntryDate' ? sort.dir : null}
                            onToggle={toggleSort}
                            className="min-w-[135px]"
                            help={<HelpIcon label="CB entry date" help={HEADER_HELP.cb_participant_entry_date} />}
                          >
                            CB Entry
                          </SortableHeader>
                          <SortableHeader
                            columnKey="high3Compensation"
                            active={sort.key === 'high3Compensation'}
                            dir={sort.key === 'high3Compensation' ? sort.dir : null}
                            align="right"
                            onToggle={toggleSort}
                            className="min-w-[125px]"
                            help={<HelpIcon label="High-3 compensation" help={HEADER_HELP.high_3_compensation} />}
                          >
                            High-3 Comp
                          </SortableHeader>
                          <SortableHeader
                            columnKey="section415Compensation"
                            active={sort.key === 'section415Compensation'}
                            dir={sort.key === 'section415Compensation' ? sort.dir : null}
                            align="right"
                            onToggle={toggleSort}
                            className="min-w-[125px]"
                            help={<HelpIcon label="§415 compensation" help={HEADER_HELP.section_415_compensation} />}
                          >
                            §415 Comp
                          </SortableHeader>
                        </>
                      )}
                      <SortableHeader
                        columnKey="pretaxPercent"
                        active={sort.key === 'pretaxPercent'}
                        dir={sort.key === 'pretaxPercent' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                        className="min-w-[105px]"
                        help={<HelpIcon label="Pre-tax %" help={HEADER_HELP.pretax_percent} />}
                      >
                        Pre-tax %
                      </SortableHeader>
                      <SortableHeader
                        columnKey="rothPercent"
                        active={sort.key === 'rothPercent'}
                        dir={sort.key === 'rothPercent' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                        className="min-w-[95px]"
                        help={<HelpIcon label="Roth %" help={HEADER_HELP.roth_percent} />}
                      >
                        Roth %
                      </SortableHeader>
                      <SortableHeader
                        columnKey="status"
                        active={sort.key === 'status'}
                        dir={sort.key === 'status' ? sort.dir : null}
                        onToggle={toggleSort}
                        className="min-w-[110px]"
                        help={<HelpIcon label="Status" help={HEADER_HELP.status} />}
                      >
                        Status
                      </SortableHeader>
                      <SortableHeader
                        columnKey="fullTime"
                        active={sort.key === 'fullTime'}
                        dir={sort.key === 'fullTime' ? sort.dir : null}
                        onToggle={toggleSort}
                        className="min-w-[90px]"
                        help={<HelpIcon label="Full time" help={HEADER_HELP.full_time} />}
                      >
                        Full time
                      </SortableHeader>
                      <SortableHeader
                        columnKey="keyEmployee"
                        active={sort.key === 'keyEmployee'}
                        dir={sort.key === 'keyEmployee' ? sort.dir : null}
                        onToggle={toggleSort}
                        className="min-w-[90px]"
                        help={<HelpIcon label="Key Employee" help={HEADER_HELP.key_employee} />}
                      >
                        Key Employee
                      </SortableHeader>
                      <SortableHeader
                        columnKey="fivePercentOwner"
                        active={sort.key === 'fivePercentOwner'}
                        dir={sort.key === 'fivePercentOwner' ? sort.dir : null}
                        onToggle={toggleSort}
                        className="min-w-[90px]"
                        help={<HelpIcon label="Owner >5%" help={HEADER_HELP.five_percent_owner} />}
                      >
                        Owner &gt;5%
                      </SortableHeader>
                      <SortableHeader
                        columnKey="ownershipPercent"
                        active={sort.key === 'ownershipPercent'}
                        dir={sort.key === 'ownershipPercent' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                        className="min-w-[90px]"
                        help={<HelpIcon label="Ownership %" help={HEADER_HELP.ownership_percent} />}
                      >
                        Ownership %
                      </SortableHeader>
                      <SortableHeader
                        columnKey="familyOf"
                        active={sort.key === 'familyOf'}
                        dir={sort.key === 'familyOf' ? sort.dir : null}
                        onToggle={toggleSort}
                        className="min-w-[130px]"
                        help={<HelpIcon label="Family Attribution" help={HEADER_HELP.family_of} />}
                      >
                        Family Attribution
                      </SortableHeader>
                      <SortableHeader
                        columnKey="allocationGroup"
                        active={sort.key === 'allocationGroup'}
                        dir={sort.key === 'allocationGroup' ? sort.dir : null}
                        onToggle={toggleSort}
                        className="min-w-[140px]"
                        help={<HelpIcon label="Allocation Class" help={HEADER_HELP.allocation_group} />}
                      >
                        Allocation Class
                      </SortableHeader>
                      <SortableHeader
                        columnKey="gender"
                        active={sort.key === 'gender'}
                        dir={sort.key === 'gender' ? sort.dir : null}
                        onToggle={toggleSort}
                        className="min-w-[80px]"
                        help={<HelpIcon label="Gender" help={HEADER_HELP.gender} />}
                      >
                        Gender
                      </SortableHeader>
                      <SortableHeader
                        columnKey="tenure"
                        active={sort.key === 'tenure'}
                        dir={sort.key === 'tenure' ? sort.dir : null}
                        onToggle={toggleSort}
                        className="min-w-[180px]"
                        help={<HelpIcon label="Tenure" help={HEADER_HELP.tenure} />}
                      >
                        Tenure
                      </SortableHeader>
                      <SortableHeader
                        columnKey="terminationDate"
                        active={sort.key === 'terminationDate'}
                        dir={sort.key === 'terminationDate' ? sort.dir : null}
                        onToggle={toggleSort}
                        className="min-w-[140px]"
                        help={<HelpIcon label="Termination Date" help={HEADER_HELP.termination_date} />}
                      >
                        Termination Date
                      </SortableHeader>
                      <SortableHeader
                        columnKey="pretaxAmount"
                        active={sort.key === 'pretaxAmount'}
                        dir={sort.key === 'pretaxAmount' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                        className="min-w-[120px]"
                        help={<HelpIcon label="Pre-tax $" help={HEADER_HELP.pretax_amount} />}
                      >
                        Pre-tax $
                      </SortableHeader>
                      <SortableHeader
                        columnKey="rothAmount"
                        active={sort.key === 'rothAmount'}
                        dir={sort.key === 'rothAmount' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                        className="min-w-[120px]"
                        help={<HelpIcon label="Roth $" help={HEADER_HELP.roth_amount} />}
                      >
                        Roth $
                      </SortableHeader>
                      <SortableHeader
                        columnKey="balance"
                        active={sort.key === 'balance'}
                        dir={sort.key === 'balance' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                        className="min-w-[140px]"
                        help={<HelpIcon label="Balance" help={HEADER_HELP.balance} />}
                      >
                        Balance
                      </SortableHeader>
                      <SortableHeader
                        columnKey="loanBalance"
                        active={sort.key === 'loanBalance'}
                        dir={sort.key === 'loanBalance' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                        className="min-w-[150px]"
                        help={<HelpIcon label="Loan Balance" help={HEADER_HELP.loan_balance} />}
                      >
                        Loan Balance
                      </SortableHeader>
                      <SortableHeader
                        columnKey="afterTaxPercent"
                        active={sort.key === 'afterTaxPercent'}
                        dir={sort.key === 'afterTaxPercent' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                        className="min-w-[110px]"
                        help={<HelpIcon label="After-tax %" help={HEADER_HELP.after_tax_percent} />}
                      >
                        After-tax %
                      </SortableHeader>
                      <SortableHeader
                        columnKey="afterTaxAmount"
                        active={sort.key === 'afterTaxAmount'}
                        dir={sort.key === 'afterTaxAmount' ? sort.dir : null}
                        align="right"
                        onToggle={toggleSort}
                        className="min-w-[120px]"
                        help={<HelpIcon label="After-tax $" help={HEADER_HELP.after_tax_amount} />}
                      >
                        After-tax $
                      </SortableHeader>
                    </tr>
                  </thead>
                  <tbody>
                    {viewRows.length === 0 && isActive && (
                      <EmptyState colSpan={employeeTableColumnCount} onClear={clearAll} />
                    )}
                    <PaginatedRows
                      inlineRows={viewRows}
                      pagedRows={visibleEmployees}
                      initialExpandedRows={EMPLOYEE_INPUT_INITIAL_RENDER_ROWS}
                      expandedRenderChunkSize={EMPLOYEE_INPUT_RENDER_CHUNK_SIZE}
                      priorityExpandedIndex={pendingFocusVisibleIndex}
                      renderRow={(emp) => {
                        const employeeIndex = employeeIndexByRef.get(emp) ?? -1
                        const rowKey = employeeRowKey(employeeIndex >= 0 ? employeeIndex : emp.id || 'unknown')
                        return (
                          <EmployeeRow
                            key={rowKey}
                            employee={emp}
                            employeeIndex={employeeIndex}
                            rowKey={rowKey}
                            isSelected={selectedIds.has(emp.id)}
                            onToggleSelect={handleToggleSelect}
                            onUpdate={handleUpdateEmployee}
                            showCbColumns={showCbColumns}
                          />
                        )
                      }}
                    />
                  </tbody>
                </table>
              </TooltipProvider>
            </ExpandableScroll>
            <ShowMoreBar total={viewRows.length} />
            <div className="mt-2 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-xs text-muted-foreground">
              <span>
                {employees.length} employee{employees.length === 1 ? '' : 's'} · Plan year {planYear}
                {selectedIds.size > 0 ? ` · ${selectedIds.size} selected` : ''}
              </span>
              <ScrollHint when="both" />
            </div>
            </ExpandableFill>
          </ExpandableFill>
        </Expandable>
      ) : (
        <div className="space-y-4 py-2">
          <CensusStartOptions
            dropZoneProps={dropZoneProps}
            isDragging={isDragging}
            isUploading={isUploading}
            onDownloadTemplate={handleDownloadTemplate}
            onLoadExamples={handleLoadExamples}
            onAddBlankRow={handleAddEmployee}
            onOpenEstimator={openEstimator}
          />

          <div className="flex justify-center">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 text-muted-foreground"
              onClick={() => setIsColumnGuideOpen(!isColumnGuideOpen)}
              aria-expanded={isColumnGuideOpen}
              aria-controls="column-guide"
            >
              <HelpCircle className="mr-1.5 h-3.5 w-3.5" />
              CSV column guide
              {isColumnGuideOpen ? <ChevronUp className="ml-1 h-3.5 w-3.5" /> : <ChevronDown className="ml-1 h-3.5 w-3.5" />}
            </Button>
          </div>

          {isColumnGuideOpen && (
            <CsvColumnGuide className="rounded-md border border-border bg-card p-3" />
          )}
        </div>
      )}
    </section>
  )
}

const EmployeeRow = memo(function EmployeeRow({ employee, employeeIndex, rowKey, isSelected, onToggleSelect, onUpdate, showCbColumns = true }) {
  const { expanded, open } = useExpandable()
  const idValidationId = `${rowKey}-id`
  const setInputError = useStore((state) => state.setInputError)
  const storedIdError = useStore((state) => state.inputErrors[idValidationId])
  const [localData, setLocalData] = useState(employee)
  const localDataRef = useRef(employee)
  const previousIdErrorMessageRef = useRef('')
  const skipNextIdErrorPublishRef = useRef(false)
  const accessibleRowName = localData.name?.trim() || localData.id || 'employee'
  const rowNumber = employeeIndex >= 0 ? employeeIndex + 1 : null
  const rowIdentifier = employee.id?.trim() || localData.id?.trim() || localData.name?.trim() || (rowNumber ? `row ${rowNumber}` : 'this row')
  const validationActive = expanded || !open
  const validationIdFor = (field) => `${rowKey}-${field}`
  // Stable target objects per (rowKey, employeeIndex) so the NumberInput/effect
  // dependency arrays don't see a new reference on every render.
  const validationTargets = useMemo(() => {
    const fields = [
      'id', 'hours_served', 'compensation', 'prior_year_compensation',
      'high_3_compensation', 'section_415_compensation',
      'pretax_percent', 'roth_percent', 'pretax_amount', 'roth_amount',
      'balance', 'loan_balance', 'after_tax_percent', 'after_tax_amount',
    ]
    return Object.fromEntries(fields.map((field) => [field, {
      kind: 'employee',
      validationId: `${rowKey}-${field}`,
      employeeIndex,
      field,
    }]))
  }, [rowKey, employeeIndex])
  const validationTargetFor = (field) => validationTargets[field]
  const idValidationTarget = validationTargets.id
  const employeeValidationLabel = (column) => `Employee ${rowIdentifier} - ${column}`
  const idErrorDraft = storedIdError?.draft !== undefined ? String(storedIdError.draft) : null
  const displayedEmployeeId = idErrorDraft !== null ? idErrorDraft : (localData.id || '')
  const idIsInvalid = validationActive && !String(displayedEmployeeId).trim()
  const idErrorLabel = employeeValidationLabel('ID')

  useEffect(() => {
    setLocalData(employee)
    localDataRef.current = employee
  }, [employee])

  useLayoutEffect(() => {
    const storedMessage = storedIdError?.message || ''
    const hadStoredMessage = previousIdErrorMessageRef.current
    previousIdErrorMessageRef.current = storedMessage

    if (hadStoredMessage && !storedMessage && !String(localData.id || '').trim()) {
      skipNextIdErrorPublishRef.current = true
      setLocalData(employee)
      localDataRef.current = employee
      return
    }

    if (storedMessage && idErrorDraft !== null && localData.id !== idErrorDraft) {
      setLocalData((prev) => {
        const next = { ...prev, id: idErrorDraft }
        localDataRef.current = next
        return next
      })
    }
  }, [employee, idErrorDraft, localData.id, storedIdError?.message])

  useEffect(() => {
    if (!validationActive) return undefined
    if (skipNextIdErrorPublishRef.current) {
      skipNextIdErrorPublishRef.current = false
      return undefined
    }

    const nextId = idErrorDraft !== null ? idErrorDraft : (localData.id || '')
    if (String(nextId).trim()) {
      setInputError(idValidationId, '')
      return undefined
    }
    if (storedIdError?.message) return undefined

    setInputError(idValidationId, {
      message: EMPLOYEE_ID_REQUIRED_MESSAGE,
      label: idErrorLabel,
      target: idValidationTarget,
      draft: nextId,
    })

    // Keep an invalid employee-ID draft when the modal copy unmounts so the
    // inline row can keep showing the blank field and blocked outputs.
    return undefined
  }, [idErrorDraft, idErrorLabel, idValidationId, idValidationTarget, localData.id, setInputError, storedIdError?.message, validationActive])

  const handleChange = (field, value) => {
    setLocalData(prev => {
      const next = { ...prev, [field]: value }
      localDataRef.current = next
      return next
    })
  }

  const handleEmployeeIdChange = (value) => {
    handleChange('id', value)

    if (!validationActive) return
    if (String(value).trim()) {
      setInputError(idValidationId, '')
      return
    }

    setInputError(idValidationId, {
      message: EMPLOYEE_ID_REQUIRED_MESSAGE,
      label: idErrorLabel,
      target: idValidationTarget,
      draft: value,
    })
  }

  const handleBlur = (_event, validationResult) => {
    if (validationResult?.valid === false) return

    if (!String(localDataRef.current.id || '').trim()) {
      if (validationActive) {
        setInputError(idValidationId, {
          message: EMPLOYEE_ID_REQUIRED_MESSAGE,
          label: idErrorLabel,
          target: idValidationTarget,
          draft: localDataRef.current.id || '',
        })
      }
      return
    }

    setInputError(idValidationId, '')
    if (onUpdate(employee.id, localDataRef.current) === false) {
      setLocalData(employee)
      localDataRef.current = employee
    }
  }

  const commitImmediateChange = (nextData) => {
    if (onUpdate(employee.id, nextData) === false) return
    setLocalData(nextData)
    localDataRef.current = nextData
  }

  return (
    <tr className={isSelected ? 'bg-primary/5' : ''}>
      <td className="sticky-col sticky-select">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => onToggleSelect(employee.id)}
          aria-label={`Select ${accessibleRowName}`}
        />
      </td>
      <td className="sticky-col sticky-employee-id">
        <Input
          aria-label={`Employee ID for ${accessibleRowName}`}
          aria-invalid={idIsInvalid ? true : undefined}
          data-validation-id={idValidationId}
          title={idIsInvalid ? EMPLOYEE_ID_REQUIRED_MESSAGE : undefined}
          value={displayedEmployeeId}
          onChange={(e) => handleEmployeeIdChange(e.target.value)}
          onBlur={handleBlur}
          className="h-7 min-w-[6.5rem] text-xs"
        />
      </td>
      <td className="sticky-col sticky-employee-name">
        <Input aria-label={`Name for ${accessibleRowName}`} value={localData.name} onChange={(e) => handleChange('name', e.target.value)} onBlur={handleBlur} placeholder="Name" className="h-7 min-w-[10rem] text-xs" />
      </td>
      <td>
        <Input aria-label={`Date of birth for ${accessibleRowName}`} type="date" value={localData.dob} onChange={(e) => handleChange('dob', e.target.value)} onBlur={handleBlur} className="h-7 text-xs" />
      </td>
      <td>
        <Input aria-label={`Hire date for ${accessibleRowName}`} type="date" value={localData.hire_date} onChange={(e) => handleChange('hire_date', e.target.value)} onBlur={handleBlur} className="h-7 text-xs" />
      </td>
      <td>
        <NumberInput validationId={validationIdFor('hours_served')} validationLabel={employeeValidationLabel('Hours served')} validationTarget={validationTargetFor('hours_served')} validationActive={validationActive} aria-label={`Hours served for ${accessibleRowName}`} value={localData.hours_served ?? ''} onValueChange={(value) => handleChange('hours_served', value)} emptyValue="" onBlur={handleBlur} className="h-7 text-right text-xs" min={0} showError={false} />
      </td>
      <td>
        <NumberInput validationId={validationIdFor('compensation')} validationLabel={employeeValidationLabel('Compensation')} validationTarget={validationTargetFor('compensation')} validationActive={validationActive} aria-label={`Compensation for ${accessibleRowName}`} value={localData.compensation} onValueChange={(value) => handleChange('compensation', value)} onBlur={handleBlur} className="h-7 text-right text-xs" min={0} showError={false} emptyMessage="Compensation is required." />
      </td>
      <td>
        <NumberInput validationId={validationIdFor('prior_year_compensation')} validationLabel={employeeValidationLabel('Prior-year compensation')} validationTarget={validationTargetFor('prior_year_compensation')} validationActive={validationActive} aria-label={`Prior-year compensation for ${accessibleRowName}`} value={localData.prior_year_compensation || ''} onValueChange={(value) => handleChange('prior_year_compensation', value)} emptyValue="" onBlur={handleBlur} className="h-7 text-right text-xs" min={0} showError={false} placeholder="Optional" />
      </td>
      {showCbColumns && (
        <>
          <td>
            <Input aria-label={`Cash-balance participant entry date for ${accessibleRowName}`} type="date" value={localData.cb_participant_entry_date || ''} onChange={(e) => handleChange('cb_participant_entry_date', e.target.value)} onBlur={handleBlur} className="h-7 text-xs" />
          </td>
          <td>
            <NumberInput validationId={validationIdFor('high_3_compensation')} validationLabel={employeeValidationLabel('High-3 compensation')} validationTarget={validationTargetFor('high_3_compensation')} validationActive={validationActive} aria-label={`High-3 compensation for ${accessibleRowName}`} value={localData.high_3_compensation || ''} onValueChange={(value) => handleChange('high_3_compensation', value)} emptyValue="" onBlur={handleBlur} className="h-7 text-right text-xs" min={0} showError={false} placeholder="Optional" />
          </td>
          <td>
            <NumberInput validationId={validationIdFor('section_415_compensation')} validationLabel={employeeValidationLabel('Section 415 compensation')} validationTarget={validationTargetFor('section_415_compensation')} validationActive={validationActive} aria-label={`Section 415 compensation for ${accessibleRowName}`} value={localData.section_415_compensation || ''} onValueChange={(value) => handleChange('section_415_compensation', value)} emptyValue="" onBlur={handleBlur} className="h-7 text-right text-xs" min={0} showError={false} placeholder="Optional" />
          </td>
        </>
      )}
      <td>
        <NumberInput validationId={validationIdFor('pretax_percent')} validationLabel={employeeValidationLabel('Pre-tax percentage')} validationTarget={validationTargetFor('pretax_percent')} validationActive={validationActive} aria-label={`Pre-tax percentage for ${accessibleRowName}`} value={localData.pretax_percent} onValueChange={(value) => handleChange('pretax_percent', value)} emptyValue="" onBlur={handleBlur} className="h-7 text-right text-xs" min={0} max={100} step="any" showError={false} />
      </td>
      <td>
        <NumberInput validationId={validationIdFor('roth_percent')} validationLabel={employeeValidationLabel('Roth percentage')} validationTarget={validationTargetFor('roth_percent')} validationActive={validationActive} aria-label={`Roth percentage for ${accessibleRowName}`} value={localData.roth_percent} onValueChange={(value) => handleChange('roth_percent', value)} emptyValue="" onBlur={handleBlur} className="h-7 text-right text-xs" min={0} max={100} step="any" showError={false} />
      </td>
      <td>
        <Select
          value={localData.status || 'Active'}
          onValueChange={(v) => {
            const nextData = { ...localData, status: v }
            commitImmediateChange(nextData)
          }}
        >
          <SelectTrigger aria-label={`Employment status for ${accessibleRowName}`} className="h-7 border-transparent bg-transparent text-xs shadow-none hover:border-input hover:bg-card">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Leave">Leave</SelectItem>
            <SelectItem value="Terminated">Terminated</SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td>
        <Select
          value={localData.full_time || 'none'}
          onValueChange={(v) => {
            const full_time = v === 'none' ? '' : v
            const nextData = { ...localData, full_time }
            commitImmediateChange(nextData)
          }}
        >
          <SelectTrigger aria-label={`Full-time status for ${accessibleRowName}`} className="h-7 border-transparent bg-transparent text-xs shadow-none hover:border-input hover:bg-card">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">-</SelectItem>
            <SelectItem value="Y">Y</SelectItem>
            <SelectItem value="N">N</SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td>
        <Select
          value={localData.key_employee || 'none'}
          onValueChange={(v) => {
            const key_employee = v === 'none' ? '' : v
            const nextData = { ...localData, key_employee }
            commitImmediateChange(nextData)
          }}
        >
          <SelectTrigger aria-label={`Key employee status for ${accessibleRowName}`} className="h-7 border-transparent bg-transparent text-xs shadow-none hover:border-input hover:bg-card">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">-</SelectItem>
            <SelectItem value="Y">Y</SelectItem>
            <SelectItem value="N">N</SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td>
        <Select
          value={localData.five_percent_owner || 'none'}
          onValueChange={(v) => {
            const five_percent_owner = v === 'none' ? '' : v
            const nextData = { ...localData, five_percent_owner }
            commitImmediateChange(nextData)
          }}
        >
          <SelectTrigger aria-label={`More-than-5% owner status for ${accessibleRowName}`} className="h-7 border-transparent bg-transparent text-xs shadow-none hover:border-input hover:bg-card">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">-</SelectItem>
            <SelectItem value="Y">Y</SelectItem>
            <SelectItem value="N">N</SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td>
        <NumberInput aria-label={`Ownership percent for ${accessibleRowName}`} value={localData.ownership_percent ?? ''} onValueChange={(value) => handleChange('ownership_percent', value)} emptyValue="" onBlur={handleBlur} className="h-7 text-right text-xs" min={0} max={100} showError={false} placeholder="Optional" />
      </td>
      <td>
        <Input aria-label={`Family of owner for ${accessibleRowName}`} value={localData.family_of || ''} onChange={(e) => handleChange('family_of', e.target.value)} onBlur={handleBlur} placeholder="Optional" className="h-7 text-xs" />
      </td>
      <td>
        <Input
          aria-label={`Allocation group for ${accessibleRowName}`}
          value={localData.allocation_group || ''}
          onChange={(e) => handleChange('allocation_group', e.target.value)}
          onBlur={handleBlur}
          placeholder="Optional"
          className="h-7 min-w-[8rem] text-xs"
        />
      </td>
      <td>
        <Select
          value={localData.gender || 'none'}
          onValueChange={(v) => {
            const gender = v === 'none' ? '' : v
            const nextData = { ...localData, gender }
            commitImmediateChange(nextData)
          }}
        >
          <SelectTrigger aria-label={`Gender for ${accessibleRowName}`} className="h-7 border-transparent bg-transparent text-xs shadow-none hover:border-input hover:bg-card">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none">-</SelectItem>
            <SelectItem value="M">M</SelectItem>
            <SelectItem value="F">F</SelectItem>
            <SelectItem value="U">Undisclosed</SelectItem>
          </SelectContent>
        </Select>
      </td>
      <td>
        <Input aria-label={`Tenure for ${accessibleRowName}`} value={localData.tenure || ''} onChange={(e) => handleChange('tenure', e.target.value)} onBlur={handleBlur} placeholder="Format: 5y 6m" className="h-7 min-w-[170px] text-xs" />
      </td>
      <td>
        <Input aria-label={`Termination date for ${accessibleRowName}`} type="date" value={localData.termination_date} onChange={(e) => handleChange('termination_date', e.target.value)} onBlur={handleBlur} className="h-7 text-xs" />
      </td>
      <td>
        <NumberInput validationId={validationIdFor('pretax_amount')} validationLabel={employeeValidationLabel('Pre-tax amount')} validationTarget={validationTargetFor('pretax_amount')} validationActive={validationActive} aria-label={`Pre-tax amount for ${accessibleRowName}`} value={localData.pretax_amount} onValueChange={(value) => handleChange('pretax_amount', value)} emptyValue="" onBlur={handleBlur} className="h-7 text-right text-xs" min={0} showError={false} />
      </td>
      <td>
        <NumberInput validationId={validationIdFor('roth_amount')} validationLabel={employeeValidationLabel('Roth amount')} validationTarget={validationTargetFor('roth_amount')} validationActive={validationActive} aria-label={`Roth amount for ${accessibleRowName}`} value={localData.roth_amount} onValueChange={(value) => handleChange('roth_amount', value)} emptyValue="" onBlur={handleBlur} className="h-7 text-right text-xs" min={0} showError={false} />
      </td>
      <td>
        <NumberInput validationId={validationIdFor('balance')} validationLabel={employeeValidationLabel('Balance')} validationTarget={validationTargetFor('balance')} validationActive={validationActive} aria-label={`Balance for ${accessibleRowName}`} value={localData.balance} onValueChange={(value) => handleChange('balance', value)} emptyValue="" onBlur={handleBlur} className="h-7 text-right text-xs" min={0} showError={false} />
      </td>
      <td>
        <NumberInput validationId={validationIdFor('loan_balance')} validationLabel={employeeValidationLabel('Loan balance')} validationTarget={validationTargetFor('loan_balance')} validationActive={validationActive} aria-label={`Loan balance for ${accessibleRowName}`} value={localData.loan_balance} onValueChange={(value) => handleChange('loan_balance', value)} emptyValue="" onBlur={handleBlur} className="h-7 text-right text-xs" min={0} showError={false} />
      </td>
      <td>
        <NumberInput validationId={validationIdFor('after_tax_percent')} validationLabel={employeeValidationLabel('After-tax percentage')} validationTarget={validationTargetFor('after_tax_percent')} validationActive={validationActive} aria-label={`After-tax percentage for ${accessibleRowName}`} value={localData.after_tax_percent} onValueChange={(value) => handleChange('after_tax_percent', value)} emptyValue="" onBlur={handleBlur} className="h-7 text-right text-xs" min={0} max={100} step="any" showError={false} placeholder="Optional" />
      </td>
      <td>
        <NumberInput validationId={validationIdFor('after_tax_amount')} validationLabel={employeeValidationLabel('After-tax amount')} validationTarget={validationTargetFor('after_tax_amount')} validationActive={validationActive} aria-label={`After-tax amount for ${accessibleRowName}`} value={localData.after_tax_amount} onValueChange={(value) => handleChange('after_tax_amount', value)} emptyValue="" onBlur={handleBlur} className="h-7 text-right text-xs" min={0} showError={false} placeholder="Optional" />
      </td>
    </tr>
  )
})
